import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { api } from '@/lib/api/client';
import { AdminLayout } from '@/components/layouts/admin-layout';
import {
  ArrowLeft,
  User,
  Mail,
  Shield,
  Calendar,
  Clock,
  Activity,
  MessageCircle,
  Video,
  AlertCircle
} from 'lucide-react';

interface AdminUserDetail {
  id: number;
  email: string;
  name: string;
  role: string;
  status: string;
  joinedAt: string;
  lastActive: string;
  loginCount: number;
  streamsCreated: number;
  commentsPosted: number;
  avatarUrl?: string;
}

export default function AdminUserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState<AdminUserDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchUser();
    }
  }, [id]);

  const fetchUser = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await api.get<{ user: AdminUserDetail } | AdminUserDetail>(`/api/admin/users/${id}`);

      if (response.success && response.data) {
        const userInfo = (response.data as { user: AdminUserDetail }).user ?? (response.data as AdminUserDetail);
        setUser(userInfo);
      } else {
        setError(response.error?.message || 'Failed to load user');
      }
    } catch (err) {
      console.error('Error fetching user:', err);
      setError('Failed to load user');
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleBadge = (role: string | undefined) => {
    const safeRole = role || 'member';
    const variants: Record<string, 'default' | 'secondary' | 'outline'> = {
      'admin': 'default',
      'media_team': 'secondary',
      'moderator': 'outline',
      'pastor': 'outline',
      'member': 'outline'
    };
    return (
      <Badge variant={variants[safeRole] || 'outline'}>
        {safeRole.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </Badge>
    );
  };

  const getStatusBadge = (status: string | undefined) => {
    const safeStatus = status || 'unknown';
    const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
      'active': 'default',
      'pending': 'secondary',
      'banned': 'destructive'
    };
    return <Badge variant={variants[safeStatus] || 'outline'}>{safeStatus}</Badge>;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';

    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <AdminLayout>
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/admin/users')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Users
          </Button>
        </div>

        <Card className="border-destructive">
          <CardContent className="p-4 @[1120px]/admin-layout:p-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Error Loading User</h3>
              <p className="text-muted-foreground mb-4">
                {error || 'User not found'}
              </p>
              <Button onClick={() => navigate('/admin/users')}>
                Return to Users
              </Button>
            </div>
          </CardContent>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex flex-col gap-3 @[900px]/admin-layout:flex-row @[900px]/admin-layout:items-center @[900px]/admin-layout:justify-between">
        <div className="flex flex-wrap items-center gap-3 @[760px]/admin-layout:gap-4">
          <Button variant="outline" onClick={() => navigate('/admin/users')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Users
          </Button>
        </div>
      </div>

      {/* User Profile Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-col items-start gap-4 @[760px]/admin-layout:flex-row @[760px]/admin-layout:items-center @[760px]/admin-layout:gap-6">
            <Avatar className="h-24 w-24">
              <AvatarImage src={user.avatarUrl} />
              <AvatarFallback className="text-2xl">
                {(user.name || user.email || 'U').charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <CardTitle className="text-xl @[760px]/admin-layout:text-2xl">{user.name}</CardTitle>
              <CardDescription className="text-base @[760px]/admin-layout:text-lg">{user.email}</CardDescription>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {getRoleBadge(user.role)}
                {getStatusBadge(user.status)}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 @[1120px]/admin-layout:space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 gap-4 @[1120px]/admin-layout:grid-cols-2 @[1120px]/admin-layout:gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Account Information</h3>

              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Email</p>
                  <p className="text-muted-foreground">{user.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Shield className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Role</p>
                  <p className="text-muted-foreground">{getRoleBadge(user.role)}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Status</p>
                  <p className="text-muted-foreground">{getStatusBadge(user.status)}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Activity Information</h3>

              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Joined</p>
                  <p className="text-muted-foreground">{formatDate(user.joinedAt)}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Last Active</p>
                  <p className="text-muted-foreground">{formatDateTime(user.lastActive)}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Login Count</p>
                  <p className="text-muted-foreground">{user.loginCount} times</p>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="border-t pt-4 @[1120px]/admin-layout:pt-6">
            <h3 className="text-lg font-semibold mb-4">User Statistics</h3>
            <div
              className="grid gap-3 @[900px]/admin-layout:gap-4"
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))' }}
            >
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Video className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Streams Created</p>
                      <p className="text-2xl font-bold">{user.streamsCreated}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <MessageCircle className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Comments Posted</p>
                      <p className="text-2xl font-bold">{user.commentsPosted}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Activity className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Total Logins</p>
                      <p className="text-2xl font-bold">{user.loginCount}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
